// src/App.js
import React, { useState, useEffect } from "react";
import {
  HashRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { SnackbarProvider } from "notistack";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase";
import { UserProvider } from "./context/UserContext";

// Components
import HomePage from "./components/HomePage";
import Login from "./components/Login";
import Signup from "./components/Signup";
import Dashboard from "./components/Dashboard";
import Forms from "./components/Forms";
import Profile from "./components/Profile";
import PrivateRoute from "./components/PrivateRoute";
import Logout from "./components/Logout";
import Layout from "./components/Layout";
import Resetpassword from "./components/Resetpassword";
import AddDashboard from "./components/AddDashboard";
import TBAdminlogin from "./components/TBAdminlogin";
import TBAdminLogout from "./components/TBAdminLogout";
import AddForm from "./components/AddForm";
import AddData from "./components/AddData";
import Users from "./components/Users";
import Data from "./components/Data";
import Projects from "./components/Projects";
import OrgRegistration from "./components/OrgRegistration";
import OrganizationsList from "./components/OrganizationList";
import Tnc from "./components/Tnc";
import OnboardingRoute from "./components/OnboardingRoute";

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthenticated(!!user);
    });
    return () => unsubscribe();
  }, []);

  if (isAuthenticated === null) {
    return <div>Loading...</div>;
  }

  return (
    <SnackbarProvider
      maxSnack={3}
      autoHideDuration={3000}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
    >
      <Router>
        <Routes>
          {/* Default route redirect */}
          <Route
            path="/"
            element={
              <Navigate
                to={isAuthenticated ? "/portal/home" : "/login"}
                replace
              />
            }
          />

          {/* Public Routes */}
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/portal/home" replace />
              ) : (
                <Login />
              )
            }
          />
          <Route path="/reset-password" element={<Resetpassword />} />

          {/* Admin routes (public for now) */}
          <Route path="/tbadmin/*" element={<TBAdminlogin />} />
          <Route path="/tbadmin-logout" element={<TBAdminLogout />} />
          <Route path="/tbadmin/signup" element={<Signup />} />
          <Route path="/tbadmin/organization" element={<OrganizationsList />} />
          <Route path="/tbadmin/register-org" element={<OrgRegistration />} />
          <Route path="/tbadmin/add-dashboard" element={<AddDashboard />} />
          <Route path="/tbadmin/add-form" element={<AddForm />} />
          <Route path="/tbadmin/add-data" element={<AddData />} />

          {/* Terms & Conditions route (protected but no Layout) */}
          <Route
            path="/tnc"
            element={
              <UserProvider>
                <PrivateRoute>
                  <Tnc />
                </PrivateRoute>
              </UserProvider>
            }
          />

          {/* Authenticated Portal Routes */}
          <Route
            path="/portal"
            element={
              <UserProvider>
                <PrivateRoute>
                  <Layout />
                </PrivateRoute>
              </UserProvider>
            }
          >
            {/* Children of portal, all gated by OnboardingRoute */}
            <Route
              path="home"
              element={
                <OnboardingRoute>
                  <HomePage />
                </OnboardingRoute>
              }
            />
            <Route
              path="project"
              element={
                <OnboardingRoute>
                  <Projects />
                </OnboardingRoute>
              }
            />
            <Route
              path="dashboard"
              element={
                <OnboardingRoute>
                  <Dashboard />
                </OnboardingRoute>
              }
            />
            <Route
              path="forms"
              element={
                <OnboardingRoute>
                  <Forms />
                </OnboardingRoute>
              }
            />
            <Route
              path="data"
              element={
                <OnboardingRoute>
                  <Data />
                </OnboardingRoute>
              }
            />
            <Route
              path="users"
              element={
                <OnboardingRoute>
                  <Users />
                </OnboardingRoute>
              }
            />
            <Route
              path="profile"
              element={
                <OnboardingRoute>
                  <Profile />
                </OnboardingRoute>
              }
            />
            <Route path="logout" element={<Logout />} />
            <Route index element={<Navigate to="home" replace />} />
          </Route>
        </Routes>
      </Router>
    </SnackbarProvider>
  );
}

export default App;
